import store from '@/store';
import { eeEnvVariables } from '@/utilities/constants';
import { KFButton } from '@kf/ic2fx';
import { Grid } from '@mui/material';
import { kf1Auth } from 'kfone-frontend-core';
import { AuthenticationStatus } from 'models/enum';
import React, { useEffect, useState } from 'react';
import { getCookie } from 'utilities/getToken';
import TypographyWithI18N from '../../../shared/components/TypographyWithI18N';
import { useKeycloakInit } from '../hooks/useKeycloakInit';
import { IAuthorizationProviderProps } from '../models/IAuth.interface';
import { loginUserSuccess } from '../stateManagement/actionsCreator';
import { setMGTExplicitSignIn } from './b2c/utils';

const ACCESS_TOKEN_COOKIE_KEY = '__kfone_access_token';
const ACCESS_TOKEN_QUERY_PARAM = 'access_token';

export const _IS_MICRO_FRONTEND__ = !!(window as any).__federation_shared__;

const shouldAvoidAuth = _IS_MICRO_FRONTEND__ ? false : true;

// Check if running on localhost
const isLocalhost = (): boolean => {
  const hostname = window.location.hostname;
  return hostname.toLowerCase() === 'localhost';
};

// Set cookie (for localhost use)
const setCookie = (name: string, value: string, days: number = 1): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

// Get token from URL query params
const getTokenFromQueryParams = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(ACCESS_TOKEN_QUERY_PARAM);
};

// Get access token based on environment
const getAccessToken = (): string | null => {
  if (isLocalhost()) {
    // Localhost: first check if token exists in cookies
    const cookieToken = getCookie(ACCESS_TOKEN_COOKIE_KEY);
    if (cookieToken) {
      return cookieToken;
    }

    // If not in cookies, get from query params and store in cookies
    const queryToken = getTokenFromQueryParams();
    if (queryToken) {
      setCookie(ACCESS_TOKEN_COOKIE_KEY, queryToken);
      return queryToken;
    }

    return null;
  } else {
    // Domain: get token from cookies
    return getCookie(ACCESS_TOKEN_COOKIE_KEY);
  }
};

export const WebAppAuthProvider = ({
  setAuthStatus,
  authStatus,
  setErrorMessage,
  isWindowSizeUnsupported,
}: IAuthorizationProviderProps) => {
  const { isAuthenticated, getTokenParsed, keycloakIns } = kf1Auth.useKeycloak();
  const { initialize } = useKeycloakInit();
  const [accessError, setAccessError] = useState<boolean | null>(false);

  useEffect(() => {
    const run = async () => {
      if (shouldAvoidAuth) {
        // Get access token based on environment (localhost vs domain)
        const accessToken = getAccessToken();
        if (!accessToken) {
          setAccessError(true);
          return;
        }

        const keycloak = await initialize(accessToken);
        store.dispatch(
          loginUserSuccess(keycloak?.token ?? '', keycloakIns?.realmAccess?.roles || []),
        );
        setAuthStatus({
          status: AuthenticationStatus.Succeeded,
          token: keycloak?.token ?? '',
        });
        setMGTExplicitSignIn(keycloak?.token ?? '');
        return;
      }

      // Keycloak flow: when authenticated, set token and succeed
      if (isAuthenticated) {
        if (keycloakIns?.token) {
          store.dispatch(
            loginUserSuccess(keycloakIns?.token, keycloakIns?.realmAccess?.roles || []),
          );
          setAuthStatus({ status: AuthenticationStatus.Succeeded, token: keycloakIns?.token });
          setMGTExplicitSignIn(keycloakIns?.token);
          return;
        }
      }

      // Not authenticated: trigger Keycloak login
      setAuthStatus({ status: AuthenticationStatus.Unauthenticated });
    };
    run();
  }, [isAuthenticated, shouldAvoidAuth]);

  // No MSAL redirect handling; Keycloak handles its own flows
  const getTokenByEmail = async (_userInput: string) => {
    setAuthStatus({ status: AuthenticationStatus.InProgress });
  };

  // Show error message if no access token found
  if (accessError) {
    return (
      <Grid
        item
        container
        flexDirection='column'
        justifyContent='center'
        alignItems='center'
        sx={{ mb: 8, px: '0.25rem', height: '100vh' }}
        data-testid='access-denied-wrapper'
      >
        <TypographyWithI18N
          i18nKey={'unsupportedDevicePageTitle'}
          className='kfic2-smallHeaderBold'
          sx={{
            mt: 3,
            mb: 2,
            fontWeight: '700 !important',
            textAlign: 'center',
          }}
          tabIndex={0}
        />
        <TypographyWithI18N
          i18nKey={'unsupportedLogin'}
          className='kfic2-bodyText'
          sx={{
            maxWidth: '30rem',
            textAlign: 'center',
            mb: '1rem',
          }}
          tabIndex={0}
        />
        <KFButton onClick={() => window.open(`${eeEnvVariables.DOMAIN_URL}`, '_blank')}>
          <TypographyWithI18N
            i18nKey={'signIn'}
            className='kfic2-bodyText'
            sx={{
              fontWeight: '700 !important',
            }}
            tabIndex={0}
          />
        </KFButton>
      </Grid>
    );
  }

  return (
    <>
      {(authStatus.status === AuthenticationStatus.Unauthenticated ||
        authStatus.status === AuthenticationStatus.InProgress) &&
        !isWindowSizeUnsupported && <h1> loading {authStatus.status}</h1>}
    </>
  );
};
